import { AuditLogEntry } from "datasets/types/interfaces/audit-log";

// Turns a structured audit-log entry into a human-readable predicate for the
// Changelog's "Summary" view, e.g. "created this dataset". The subject is the
// entry's actor, rendered as its own column. The mapping mirrors the log_*
// methods in the catalog AuditService; any pair it doesn't recognize falls back
// to a generic predicate so nothing renders blank.

// "snowflake.MY_DB.RAW.IRIS" -> "RAW.IRIS" (schema.table). A FQN without the
// leading "snowflake.<db>." prefix is returned unchanged.
function shortenTableFqn(fqn: string): string {
  const parts = fqn.split(".");
  if (parts.length > 2 && parts[0].toLowerCase() === "snowflake") {
    return parts.slice(2).join(".");
  }
  return fqn;
}

function humanizeFields(fields: string[]): string {
  if (fields.length <= 1) {
    return fields[0] ?? "metadata";
  }
  if (fields.length === 2) {
    return `${fields[0]} and ${fields[1]}`;
  }
  return `${fields.slice(0, -1).join(", ")}, and ${fields[fields.length - 1]}`;
}

// `owned`/`referenced` is Catalog's vocabulary for whether a table lives in one of
// the dataset's dedicated schemas. Users care about what that grants them, so show
// the access it implies instead. Anything else is shown verbatim rather than guessed
// at, so a new association type can't silently render as the wrong access level.
function humanizeAssociationType(associationType: string): string {
  switch (associationType.toLowerCase()) {
    case "referenced":
      return "read-only";
    case "owned":
      return "read/write";
    default:
      return associationType;
  }
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

export function auditPredicate(entry: AuditLogEntry): string {
  const { action, object, object_type: objectType, metadata } = entry;
  const meta = metadata ?? {};

  const accessLevel = stringValue(meta.access_level);
  const accessSuffix = accessLevel ? ` with ${accessLevel} permissions` : "";
  const version = typeof meta.version === "number" ? meta.version : undefined;
  const versionSuffix = version !== undefined ? ` (v${version})` : "";
  const changedFields = Array.isArray(meta.fields)
    ? meta.fields.filter((field): field is string => typeof field === "string")
    : [];
  const associationType = stringValue(meta.association_type);
  const previousAssociationType = stringValue(meta.previous_association_type);

  switch (`${action}|${objectType}`) {
    case "Created|Dataset":
      return "created this dataset";
    case "Created|Audit Log":
      return "started tracking changes to this dataset";
    case "Shared|User":
      return `shared this dataset with ${object}${accessSuffix}`;
    case "Shared|Group":
      return `shared this dataset with the ${object} group${accessSuffix}`;
    case "Unshared|User":
      return `removed ${object}'s access to this dataset`;
    case "Unshared|Group":
      return `removed the ${object} group's access to this dataset`;
    case "Added|Table":
      return `added a reference to Snowflake table ${shortenTableFqn(object)}`;
    case "Removed|Table":
      return `removed the reference to Snowflake table ${shortenTableFqn(object)}`;
    // As of this writing, the only use case for changing a table reference is to change the association type
    case "Updated|Table": {
      const table = shortenTableFqn(object);
      if (!associationType) {
        return `changed the reference to Snowflake table ${table}`;
      }
      const to = humanizeAssociationType(associationType);
      if (!previousAssociationType) {
        return `changed the reference to Snowflake table ${table} to ${to}`;
      }
      const from = humanizeAssociationType(previousAssociationType);
      return `changed the reference to Snowflake table ${table} from ${from} to ${to}`;
    }
    case "Added|File Prefix":
      return `added a reference to file prefix ${object}`;
    case "Removed|File Prefix":
      return `removed the reference to file prefix ${object}`;
    case "Added|Dashboard":
      return `added a reference to dashboard ${object}`;
    case "Removed|Dashboard":
      return `removed the reference to dashboard ${object}`;
    case "Added|Tag":
      return `added the tag ${object}`;
    case "Removed|Tag":
      return `removed the tag ${object}`;
    case "Enabled|Preview Mode":
      return "enabled preview mode";
    case "Disabled|Preview Mode":
      return "disabled preview mode";
    case "Enabled|AI Agents":
      return "enabled AI agents";
    case "Disabled|AI Agents":
      return "disabled AI agents";
    case "Added|Agent Metadata":
      return `updated the AI agent metadata${versionSuffix}`;
    case "Removed|Agent Metadata":
      return "cleared the AI agent metadata";
    case "Added|Agent Instructions":
      return `updated the AI agent instructions${versionSuffix}`;
    case "Updated|Dataset":
      return `updated the dataset's ${humanizeFields(changedFields)}`;
    case "Deleted|Dataset":
      return "deleted this dataset";
    case "Updated|Sample Prompts":
      return "updated the sample prompts";
    case "Updated|Conversation Shortcuts":
      return "updated the conversation shortcuts";
    case "Updated|External MCP Access":
      return "updated external MCP access";
    default:
      return `${action.toLowerCase()} ${objectType} ${object}`;
  }
}
